import { isAxiosError, AxiosResponse, AxiosError } from "axios";
import { reportApiError } from "../../../../sentry";

export const APIProviderFactory: IAPIProviderFactory = ({
  API,
  apiName,
  analyticsController,
}) => {
  const GET = async (
    api: string,
    queryStringParameters = {},
    headers = {},
    signal?: AbortSignal
  ) => {
    try {
      const apiInstance: AxiosResponse = await API.get(apiName, api, {
        headers,
        queryStringParameters,
        signal,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry (not just authenticated ones)
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      if (
        (status && status !== 404 && status !== 401) ||
        (!status && isAxios)
      ) {
        reportApiError(
          new Error(`API GET failed: ${axiosError.message || "Unknown error"}`),
          {
            method: "GET",
            url,
            status,
            requestBody: queryStringParameters,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const GET_TEXT = async (
    api: string,
    queryStringParameters = {},
    headers = {},
    signal?: AbortSignal
  ): Promise<
    | { success: true; data: string }
    | { success: false; status: number; body: string }
  > => {
    if (/^https?:\/\//i.test(api)) {
      try {
        const url = new URL(api);
        for (const [key, value] of Object.entries(queryStringParameters)) {
          if (value !== undefined && value !== null) {
            url.searchParams.set(key, String(value));
          }
        }
        const response = await fetch(url.toString(), {
          method: "GET",
          headers: headers as HeadersInit,
          signal,
        });
        const body = await response.text();
        if (!response.ok) {
          const status = response.status;
          if ((status && status !== 404 && status !== 401) || !status) {
            reportApiError(new Error(`API GET_TEXT failed: HTTP ${status}`), {
              method: "GET",
              url: url.toString(),
              status,
              requestBody: queryStringParameters,
              responseBody: body,
            });
          }
          return { success: false, status, body };
        }
        return { success: true, data: body };
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        reportApiError(new Error(`API GET_TEXT failed: ${message}`), {
          method: "GET",
          url: api,
          requestBody: queryStringParameters,
        });
        throw new Error("Something went wrong!");
      }
    }

    try {
      const apiInstance: AxiosResponse<string> = await API.get(apiName, api, {
        headers,
        queryStringParameters,
        signal,
        response: true,
        responseType: "text",
      } as Parameters<typeof API.get>[2]);
      const status = apiInstance.status;
      if (status < 200 || status >= 300) {
        return {
          success: false,
          status,
          body: apiInstance.data ?? "",
        };
      }
      return { success: true, data: apiInstance.data ?? "" };
    } catch (error: unknown) {
      const axiosError = error as AxiosError<string>;
      const isAxios = isAxiosError(axiosError);
      if (isAxios && axiosError.response) {
        const status = axiosError.response.status;
        const raw = axiosError.response.data;
        const body = typeof raw === "string" ? raw : JSON.stringify(raw);
        const url = axiosError.config?.url;
        if ((status && status !== 404 && status !== 401) || !status) {
          reportApiError(
            new Error(
              `API GET_TEXT failed: ${axiosError.message || "Unknown error"}`
            ),
            {
              method: "GET",
              url,
              status,
              requestBody: queryStringParameters,
              responseBody: raw,
            }
          );
        }
        return { success: false, status, body };
      }
      throw new Error("Something went wrong!");
    }
  };

  const POST = async (api: string, body = {}, headers = {}) => {
    try {
      const apiInstance: AxiosResponse = await API.post(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      if (
        (status && status !== 404 && status !== 401) ||
        (!status && isAxios)
      ) {
        reportApiError(
          new Error(
            `API POST failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "POST",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const PUT = async (api: string, body = {}, headers = {}) => {
    try {
      const apiInstance: AxiosResponse = await API.put(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      if (
        (status && status !== 404 && status !== 401) ||
        (!status && isAxios)
      ) {
        reportApiError(
          new Error(`API PUT failed: ${axiosError.message || "Unknown error"}`),
          {
            method: "PUT",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const PATCH = async (api: string, body = {}, headers = {}) => {
    try {
      const apiInstance: AxiosResponse = await API.patch(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      if (
        (status && status !== 404 && status !== 401) ||
        (!status && isAxios)
      ) {
        reportApiError(
          new Error(
            `API PATCH failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "PATCH",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  const DELETE = async (api: string, body = {}, headers = {}) => {
    try {
      const apiInstance: AxiosResponse = await API.del(apiName, api, {
        headers,
        body,
        response: true,
      });
      return apiInstance.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      const isAxios = isAxiosError(axiosError);

      // Report all API errors to Sentry
      const status = isAxios ? axiosError.response?.status : undefined;
      const url = isAxios ? axiosError.config?.url : undefined;

      if (
        (status && status !== 404 && status !== 401) ||
        (!status && isAxios)
      ) {
        reportApiError(
          new Error(
            `API DELETE failed: ${axiosError.message || "Unknown error"}`
          ),
          {
            method: "DELETE",
            url,
            status,
            requestBody: body,
            responseBody: isAxios ? axiosError.response?.data : undefined,
          }
        );
      }

      if (isAxios) {
        return axiosError.response?.data;
      }
      throw new Error("Something went wrong!");
    }
  };

  return {
    GET,
    GET_TEXT,
    POST,
    PUT,
    PATCH,
    DELETE,
  };
};
